import { Outlet } from "react-router-dom";
import TenantBrandIdentity, { IDENTITY_GRAPHIT, IDENTITY_TENANT } from "@/components/branding/TenantBrandIdentity";
import BrandingAttribution from "@/components/branding/BrandingAttribution";
import { useTheme } from "@/theme/useTheme";
import { useSkin } from "@/theme/skin";
import { GraphitBrand } from "./GraphitPaneHeader";

/**
 * Shared chrome for the public auth family (login, signup, password reset,
 * email verification, MFA challenge, invite accept): a centered column, the
 * tenant's mark shown once at the top, and the "Powered by Graphit"
 * attribution once at the bottom. Each page renders only its own Card
 * content - previously every page re-implemented (or omitted) this chrome
 * itself, so the card's vertical position and the presence of the logo
 * drifted from page to page (UI audit A1).
 *
 * Used two ways: as a router layout (`<Route element={<AuthLayout />}>`,
 * page content arrives via `<Outlet/>`) and, for a page reached outside that
 * route tree (e.g. InviteAcceptPage - see routes/index.jsx), as a plain
 * wrapper component passed `children` directly.
 *
 * `identity="graphit"` shows Graphit's mark and name rather than the signed-in
 * tenant's, and drops the then-redundant "Powered by Graphit" line. The Rock
 * link consent page uses it: the organization is chosen there, so showing a
 * tenant's brand above the choice would mislabel whose Rock is being linked.
 *
 * @param {Object} [props]
 * @param {React.ReactNode} [props.children]
 * @param {"tenant"|"graphit"} [props.identity="tenant"]
 * @returns {JSX.Element}
 */
export default function AuthLayout({ children, identity = IDENTITY_TENANT }) {
  const isGraphitIdentity = identity === IDENTITY_GRAPHIT;
  const skin = useSkin();
  const { branding } = useTheme();
  // Fluent 3D: Graphit's own pages (and any host with no tenant branding) carry the clay emblem.
  const showGraphitBrand = skin === "fluent3d" && (isGraphitIdentity || !branding?.tenant_id);
  return (
    <div data-auth-layout className="h-full overflow-y-auto bg-canvas px-4 py-8 sm:py-12">
      <div className="mx-auto flex min-h-full w-full max-w-lg flex-col justify-center">
        <div data-auth-brand className="mb-6 flex items-center border-b border-line pb-6">
          {showGraphitBrand ? <GraphitBrand /> : <TenantBrandIdentity
            identity={identity}
            className="max-w-full"
            markClassName="size-10 text-body-lg"
            nameClassName="max-w-full text-title"
          />}
        </div>
        {children ?? <Outlet />}
        {!isGraphitIdentity && skin !== "fluent3d" && <BrandingAttribution className="py-4 text-center" />}
      </div>
    </div>
  );
}
